/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { markUpdateAvailable } from '@shared/utils/chunk-retry';

const POLL_INTERVAL = 60_000;
const IDLE_TIMEOUT = 5 * 60 * 1000;
const IDLE_CHECK_INTERVAL = 10_000;
const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll'] as const;

declare const __APP_BUILD_ID__: string;

/**
 * Registers passive activity listeners that stamp the last-activity ref, and
 * returns a teardown that removes them.
 *
 * @param {React.MutableRefObject<number>} lastActivityRef - Ref holding the last activity timestamp.
 * @returns {() => void} Cleanup function that detaches every listener.
 */
function registerActivityTracking(lastActivityRef: React.MutableRefObject<number>): () => void {
  const markActivity = () => { lastActivityRef.current = Date.now(); };
  ACTIVITY_EVENTS.forEach((evt) =>
    window.addEventListener(evt, markActivity, { passive: true })
  );
  return () => ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActivity));
}

/**
 * Fetches `/version.json` and reports whether the deployed build id differs
 * from the currently running one.
 *
 * @returns {Promise<boolean>} True when a newer build id is available.
 */
async function fetchHasNewBuild(): Promise<boolean> {
  const res = await fetch('/version.json?_=' + Date.now());
  if (!res.ok) return false;
  const { buildId } = await res.json();
  return Boolean(buildId) && buildId !== __APP_BUILD_ID__;
}

/**
 * Runs one poll cycle: when a new build is detected, marks the update available
 * through the provided callback. Network errors are swallowed silently.
 *
 * @param {() => void} onUpdate - Callback invoked when a new build is found.
 * @returns {Promise<void>} Resolves after the poll completes.
 */
async function pollForUpdate(onUpdate: () => void): Promise<void> {
  try {
    if (await fetchHasNewBuild()) onUpdate();
  } catch {
    /* silent - network error should not affect UX */
  }
}

/**
 * Reloads the page when an update is pending and the user has been idle past
 * the timeout threshold.
 *
 * @param {React.MutableRefObject<number>} lastActivityRef - Last activity timestamp ref.
 * @param {React.MutableRefObject<boolean>} updateRef - Ref flagging a pending update.
 * @returns {void}
 */
function reloadWhenIdle(
  lastActivityRef: React.MutableRefObject<number>,
  updateRef: React.MutableRefObject<boolean>
): void {
  const idleMs = Date.now() - lastActivityRef.current;
  if (idleMs >= IDLE_TIMEOUT && updateRef.current) {
    window.location.reload();
  }
}

/**
 * Detects newly deployed app versions and reloads the page when it is safe.
 *
 * Polls `/version.json` for a changed build id, then reloads either after the
 * user has been idle past a threshold or on the next route change (via the
 * returned callback), so an update is never applied mid-interaction.
 *
 * @returns `updateAvailable` flag and an `onRouteChange` callback to trigger a
 * deferred reload when navigation happens.
 */
export function useAutoUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const updateAvailableRef = useRef(false);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => registerActivityTracking(lastActivityRef), []);

  useEffect(() => {
    const markUpdate = () => {
      setUpdateAvailable(true);
      updateAvailableRef.current = true;
      markUpdateAvailable();
    };
    const interval = setInterval(() => pollForUpdate(markUpdate), POLL_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!updateAvailable) return;
    const idleCheck = setInterval(
      () => reloadWhenIdle(lastActivityRef, updateAvailableRef),
      IDLE_CHECK_INTERVAL
    );
    return () => clearInterval(idleCheck);
  }, [updateAvailable]);

  const onRouteChange = useCallback(() => {
    if (updateAvailableRef.current) window.location.reload();
  }, []);

  return { updateAvailable, onRouteChange };
}
